// apps/frontend/src/hooks/useCart.ts
// 🛒 Hook Cart Ultra-Premium Enterprise EXTENDED
// Compatible avec votre architecture useOrders existante
// Version: 3.0.0 | Sprint: 5B | Quality: Enterprise Premium + Vinted-like

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import { useTranslation } from '@mode-app/shared';
import cartService from '../services/cartService';
import { analytics } from '../lib/analytics';
import { haptic } from '../lib/haptic';
import type {
  Cart,
  CartItem,
  CartVendorGroup,
  CartSummary,
  AddToCartRequest,
  UpdateCartItemRequest,
  RemoveFromCartRequest,
  SelectShippingMethodRequest,
  // 🆕 NOUVELLES INTERFACES VINTED-LIKE
  SetDeliveryMethodRequest,
  SetServicePointRequest,
  SetHomeAddressRequest,
  RefreshShippingQuoteRequest,
  DeliveryMethod,
  ServicePoint,
  ShippingQuote,
  CartActionType,
  Address,
} from '@mode-app/shared';

// ============================================================================
// 🎯 TYPES HOOK PREMIUM ÉTENDUS
// ============================================================================

export interface UseCartState {
  // 📦 Données principales
  cart: Cart | null;
  summary: CartSummary | null;

  // 📊 États de chargement granulaires (pattern comme useOrders)
  isLoading: boolean;
  isAddingItem: boolean;
  isUpdatingItem: boolean;
  isRemovingItem: boolean;
  isSelectingShipping: boolean;
  isClearing: boolean;
  isSyncing: boolean;

  // 🆕 NOUVEAUX ÉTATS VINTED-LIKE
  isCalculatingShipping: boolean;
  isSelectingServicePoint: boolean;
  isSettingDeliveryMethod: boolean;
  isRefreshingQuote: boolean;

  // ❌ Erreurs spécifiques par action
  loadError: string | null;
  addError: string | null;
  updateError: string | null;
  removeError: string | null;
  shippingError: string | null;
  generalError: string | null;

  // 🆕 NOUVELLES ERREURS VINTED-LIKE
  deliveryMethodError: string | null;
  servicePointError: string | null;
  shippingQuoteError: string | null;

  // ⚠️ Warnings UX
  warnings: string[];

  // 📈 Métriques
  lastActionAt: string | null;
  retryCount: number;

  // 🆕 CACHE SHIPPING VINTED-LIKE
  lastShippingCalculation: string | null;
  servicePointsCache: { [vendorId: string]: ServicePoint[] };
  _renderKey?: number; // ← Pour forcer re-render React
}

export interface UseCartOptions {
  // 🔄 Configuration retry
  enableRetry?: boolean;
  maxRetries?: number;

  // 📱 UX Configuration
  showToasts?: boolean;
  autoSync?: boolean;

  // 📊 Analytics
  enableAnalytics?: boolean;
  telemetryData?: Record<string, any>;

  // 🆕 NOUVELLES OPTIONS VINTED-LIKE
  enableShippingCache?: boolean;
  shippingCacheTTL?: number; // minutes
  autoRefreshQuotes?: boolean;
  geolocationForServicePoints?: boolean;
}

// ============================================================================
// 🎣 HOOK PRINCIPAL ÉTENDU
// ============================================================================

export const useCart = (options: UseCartOptions = {}) => {
  const {
    enableRetry = true,
    maxRetries = 3,
    showToasts = true,
    autoSync = false,
    enableAnalytics = true,
    telemetryData = {},
    // 🆕 NOUVELLES OPTIONS
    enableShippingCache = true,
    shippingCacheTTL = 15, // 15 minutes
    autoRefreshQuotes = false,
    geolocationForServicePoints = true,
  } = options;

  // Dependencies hooks - VOS HOOKS EXISTANTS
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const { t, locale } = useTranslation();

  // État principal étendu
  const [state, setState] = useState<UseCartState>({
    cart: null,
    summary: null,
    isLoading: false,
    isAddingItem: false,
    isUpdatingItem: false,
    isRemovingItem: false,
    isSelectingShipping: false,
    isClearing: false,
    isSyncing: false,
    // 🆕 NOUVEAUX ÉTATS
    isCalculatingShipping: false,
    isSelectingServicePoint: false,
    isSettingDeliveryMethod: false,
    isRefreshingQuote: false,
    loadError: null,
    addError: null,
    updateError: null,
    removeError: null,
    shippingError: null,
    generalError: null,
    // 🆕 NOUVELLES ERREURS
    deliveryMethodError: null,
    servicePointError: null,
    shippingQuoteError: null,
    warnings: [],
    lastActionAt: null,
    retryCount: 0,
    // 🆕 NOUVEAU CACHE
    lastShippingCalculation: null,
    servicePointsCache: {},
  });

  // Refs pour éviter stale closures
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const quoteRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const loadCartRef = useRef<(() => Promise<void>) | null>(null);

  // ============================================================================
  // 🔧 UTILITAIRES INTERNES ÉTENDUS
  // ============================================================================

  const executeWithRetry = useCallback(
    async <T>(
      operation: () => Promise<T>,
      actionType: CartActionType,
      onProgress?: (message: string) => void
    ): Promise<T> => {
      let lastError: any;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          onProgress?.(
            t(`cart.${actionType.toLowerCase()}.progress`, {
              defaultValue: `${actionType} en cours...`,
            })
          );

          const result = await operation();

          // Succès - reset retry count
          setState(prev => ({
            ...prev,
            retryCount: 0,
            [`${actionType.toLowerCase()}Error`]: null,
          }));

          // Analytics
          if (
            enableAnalytics &&
            typeof window !== 'undefined' &&
            (window as any).gtag
          ) {
            (window as any).gtag(
              'event',
              `cart_${actionType.toLowerCase()}_success`,
              {
                attempt,
                user_id: user?.id,
                locale,
                ...telemetryData,
              }
            );
          }

          return result;
        } catch (error: any) {
          lastError = error;

          if (attempt < maxRetries && enableRetry) {
            setState(prev => ({ ...prev, retryCount: attempt }));

            const delay = Math.pow(2, attempt - 1) * 1000 + Math.random() * 500;

            onProgress?.(
              t('cart.retrying', {
                defaultValue: `Nouvelle tentative dans ${Math.round(delay / 1000)}s...`,
              })
            );

            await new Promise(resolve => {
              retryTimeoutRef.current = setTimeout(resolve, delay);
            });
          } else {
            // Échec définitif
            const errorMessage =
              error.message ||
              t(`cart.${actionType.toLowerCase()}.error`, {
                defaultValue: `Erreur lors de ${actionType}`,
              });

            setState(prev => ({
              ...prev,
              [`${actionType.toLowerCase()}Error`]: errorMessage,
              generalError: errorMessage,
              retryCount: 0,
            }));

            if (showToasts) {
              showError(errorMessage);
            }

            // Analytics erreur
            if (
              enableAnalytics &&
              typeof window !== 'undefined' &&
              (window as any).gtag
            ) {
              (window as any).gtag(
                'event',
                `cart_${actionType.toLowerCase()}_error`,
                {
                  error_code: error.code || 'UNKNOWN',
                  attempt,
                  user_id: user?.id,
                  locale,
                  ...telemetryData,
                }
              );
            }

            throw error;
          }
        }
      }

      throw lastError;
    },
    [
      maxRetries,
      enableRetry,
      enableAnalytics,
      showToasts,
      showError,
      t,
      locale,
      user?.id,
      telemetryData,
    ]
  );

  // 🆕 HELPERS CACHE SHIPPING
  const isShippingCacheExpired = useCallback(
    (timestamp: string | null): boolean => {
      if (!timestamp || !enableShippingCache) return true;
      const now = Date.now();
      const cacheTime = new Date(timestamp).getTime();
      return now - cacheTime > shippingCacheTTL * 60 * 1000;
    },
    [enableShippingCache, shippingCacheTTL]
  );

  // ============================================================================
  // 📦 MÉTHODES PRINCIPALES EXISTANTES (CONSERVÉES)
  // ============================================================================

  /**
   * 🔄 Charger le panier
   */
  const loadCart = useCallback(async (): Promise<void> => {
    if (!locale) return;

    setState(prev => ({ ...prev, isLoading: true, loadError: null }));

    try {
      const response = await executeWithRetry(
        () => cartService.getCart(locale),
        'LOAD_CART'
      );

      setState(prev => ({
        ...prev,
        cart: response.cart,
        warnings: response.warnings || [],
        lastActionAt: new Date().toISOString(),
      }));

      // Charger le résumé
      const summary = await cartService.getCartSummary(locale);
      setState(prev => ({ ...prev, summary }));
    } catch (error: any) {
      console.error('Load cart error:', error);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [locale, executeWithRetry]);

  // Assigner loadCart à la ref de manière sécurisée
  useEffect(() => {
    loadCartRef.current = loadCart;
  }, [loadCart]);

  /**
   * ➕ Ajouter un article
   */
  const addToCart = useCallback(
    async (productId: string, quantity: number = 1): Promise<void> => {
      if (!locale || !isAuthenticated) {
        if (showToasts) {
          showError(
            t('cart.errors.authRequired', { defaultValue: 'Connexion requise' })
          );
        }
        return;
      }

      setState(prev => ({ ...prev, isAddingItem: true, addError: null }));

      try {
        const request: AddToCartRequest = {
          productId,
          quantity,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.addToCart(request),
          'ADD_ITEM'
        );

        // 🔄 MISE À JOUR IMMÉDIATE + REFRESH FORCE
        setState(prev => ({
          ...prev,
          cart: response.cart
            ? {
                ...response.cart,
                itemsByVendor: [...response.cart.itemsByVendor],
              }
            : null,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
          _renderKey: Date.now(),
        }));

        analytics.addToCart(productId);
        // Haptic success : l'user sent que l'ajout panier a "pris"
        haptic.success();
        // Notifie les autres instances de useCart (Layout CartIndicator,
        // etc.) pour qu'elles re-fetch leur state.
        window.dispatchEvent(new CustomEvent('glowp:cart-changed'));

        // Success toast
        if (showToasts) {
          showSuccess(
            t('cart.itemAdded', {
              defaultValue: 'Article ajouté au panier',
            })
          );
        }
      } catch (error: any) {
        console.error('Add to cart error:', error);
      } finally {
        setState(prev => ({ ...prev, isAddingItem: false }));
      }
    },
    [
      locale,
      isAuthenticated,
      executeWithRetry,
      showToasts,
      showSuccess,
      showError,
      t,
    ]
  );

  /**
   * 🔄 Mettre à jour un article
   */
  const updateCartItem = useCallback(
    async (itemId: string, quantity: number): Promise<void> => {
      if (!locale) return;

      if (quantity <= 0) {
        await removeFromCart(itemId);
        return;
      }

      setState(prev => ({ ...prev, isUpdatingItem: true, updateError: null }));

      try {
        const request: UpdateCartItemRequest = {
          itemId,
          quantity,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.updateCartItem(request),
          'UPDATE_ITEM'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart
            ? {
                ...response.cart,
                itemsByVendor: [...response.cart.itemsByVendor],
              }
            : null,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
          _renderKey: Date.now(),
        }));

        analytics.cartUpdated(itemId, quantity);

        // Refresh summary
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));
      } catch (error: any) {
        console.error('Update cart item error:', error);
      } finally {
        setState(prev => ({ ...prev, isUpdatingItem: false }));
      }
    },
    [locale, executeWithRetry]
  );

  /**
   * 🗑️ Supprimer un article
   */
  const removeFromCart = useCallback(
    async (itemId: string): Promise<void> => {
      if (!locale) return;

      setState(prev => ({ ...prev, isRemovingItem: true, removeError: null }));

      try {
        const request: RemoveFromCartRequest = {
          itemId,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.removeFromCart(request),
          'REMOVE_ITEM'
        );

        // ✅ Solution ChatGPT #1 + #2 combinées + CLONAGE
        setState(prev => ({
          ...prev,
          cart: response.cart
            ? {
                ...response.cart,
                itemsByVendor: [...response.cart.itemsByVendor],
              }
            : null,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
          _renderKey: Date.now(),
        }));

        analytics.removeFromCart(itemId);

        if (showToasts) {
          showSuccess(
            t('cart.itemRemoved', {
              defaultValue: 'Article retiré du panier',
            })
          );
        }

        // 🔥 FORCE RELOAD APRÈS SUPPRESSION
        await loadCart();
        // Notifie les autres instances de useCart
        window.dispatchEvent(new CustomEvent('glowp:cart-changed'));
      } catch (error: any) {
        console.error('❌ Remove from cart error:', error);
      } finally {
        setState(prev => ({ ...prev, isRemovingItem: false }));
      }
    },
    [locale, executeWithRetry, showToasts, showSuccess, showError, t, loadCart]
  );

  /**
   * 🚚 Sélectionner méthode de livraison (EXISTANTE)
   */
  const selectShippingMethod = useCallback(
    async (vendorId: string, shippingMethodId: string): Promise<void> => {
      if (!locale) return;

      setState(prev => ({
        ...prev,
        isSelectingShipping: true,
        shippingError: null,
      }));

      try {
        const request: SelectShippingMethodRequest = {
          vendorId,
          shippingMethodId,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.selectShippingMethod(request),
          'SELECT_SHIPPING'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
        }));

        // Refresh summary
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));
      } catch (error: any) {
        console.error('Select shipping method error:', error);
      } finally {
        setState(prev => ({ ...prev, isSelectingShipping: false }));
      }
    },
    [locale, executeWithRetry]
  );

  /**
   * 🧹 Vider le panier
   */
  const clearCart = useCallback(async (): Promise<void> => {
    if (!locale) return;

    setState(prev => ({ ...prev, isClearing: true, generalError: null }));

    try {
      const response = await executeWithRetry(
        () => cartService.clearCart(locale),
        'CLEAR_CART'
      );

      setState(prev => ({
        ...prev,
        cart: response.cart,
        warnings: [],
        lastActionAt: new Date().toISOString(),
        _renderKey: Date.now(), // ← AJOUTEZ CETTE LIGNE
      }));

      if (showToasts) {
        showSuccess(
          t('cart.cleared', {
            defaultValue: 'Panier vidé',
          })
        );
      }

      // Refresh summary
      const summary = await cartService.getCartSummary(locale);
      setState(prev => ({ ...prev, summary }));
    } catch (error: any) {
      console.error('Clear cart error:', error);
    } finally {
      setState(prev => ({ ...prev, isClearing: false }));
    }
  }, [locale, executeWithRetry, showToasts, showSuccess, t]);

  /**
   * 🔄 Actualiser les prix et disponibilités
   */
  const refreshCart = useCallback(async (): Promise<void> => {
    await loadCart();
  }, [loadCart]);

  // ============================================================================
  // 🆕 NOUVELLES MÉTHODES VINTED-LIKE
  // ============================================================================

  /**
   * 🚚 Définir la méthode de livraison pour un vendeur
   */
  const setDeliveryMethod = useCallback(
    async (vendorId: string, method: DeliveryMethod): Promise<void> => {
      if (!locale) return;

      setState(prev => ({
        ...prev,
        isSettingDeliveryMethod: true,
        deliveryMethodError: null,
      }));

      try {
        const request: SetDeliveryMethodRequest = {
          vendorId,
          method,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.setDeliveryMethod(request),
          'SET_DELIVERY_METHOD'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
        }));

        if (showToasts) {
          showSuccess(
            t('cart.deliveryMethodSet', {
              defaultValue: 'Méthode de livraison mise à jour',
            })
          );
        }

        // Refresh summary
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));

        // Si relay/locker, charger les points de service
        if (method === 'relay' || method === 'locker') {
          await loadServicePoints(vendorId);
        }
      } catch (error: any) {
        console.error('Set delivery method error:', error);
      } finally {
        setState(prev => ({ ...prev, isSettingDeliveryMethod: false }));
      }
    },
    [locale, executeWithRetry, showToasts, showSuccess, t]
  );

  /**
   * 📍 Sélectionner un point de service
   */
  const setServicePoint = useCallback(
    async (vendorId: string, servicePoint: ServicePoint): Promise<void> => {
      if (!locale) return;

      setState(prev => ({
        ...prev,
        isSelectingServicePoint: true,
        servicePointError: null,
      }));

      try {
        const request: SetServicePointRequest = {
          vendorId,
          servicePoint,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.setServicePoint(request),
          'SET_SERVICE_POINT'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
        }));

        if (showToasts) {
          showSuccess(
            t('cart.servicePointSelected', {
              defaultValue: `Point relais sélectionné: ${servicePoint.name}`,
            })
          );
        }

        // Refresh summary & quote
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));

        // Auto refresh shipping quote après sélection point
        await refreshShippingQuote(vendorId);
      } catch (error: any) {
        console.error('Set service point error:', error);
      } finally {
        setState(prev => ({ ...prev, isSelectingServicePoint: false }));
      }
    },
    [locale, executeWithRetry, showToasts, showSuccess, t]
  );

  /**
   * 🏠 Définir l'adresse de domicile
   */
  const setHomeAddress = useCallback(
    async (vendorId: string, address: Address): Promise<void> => {
      if (!locale) return;

      setState(prev => ({
        ...prev,
        isSettingDeliveryMethod: true,
        deliveryMethodError: null,
      }));

      try {
        const request: SetHomeAddressRequest = {
          vendorId,
          address,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.setHomeAddress(request),
          'SET_HOME_ADDRESS'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
        }));

        if (showToasts) {
          showSuccess(
            t('cart.homeAddressSet', {
              defaultValue: 'Adresse de livraison mise à jour',
            })
          );
        }

        // Refresh summary & quote
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));

        // Auto refresh shipping quote après changement adresse
        await refreshShippingQuote(vendorId);
      } catch (error: any) {
        console.error('Set home address error:', error);
      } finally {
        setState(prev => ({ ...prev, isSettingDeliveryMethod: false }));
      }
    },
    [locale, executeWithRetry, showToasts, showSuccess, t]
  );

  /**
   * 💰 Actualiser le devis de livraison pour un vendeur
   */
  const refreshShippingQuote = useCallback(
    async (vendorId: string): Promise<ShippingQuote | null> => {
      if (!locale) return null;

      setState(prev => ({
        ...prev,
        isRefreshingQuote: true,
        shippingQuoteError: null,
      }));

      try {
        const request: RefreshShippingQuoteRequest = {
          vendorId,
          locale,
        };

        const response = await executeWithRetry(
          () => cartService.refreshShippingQuote(request),
          'REFRESH_QUOTE'
        );

        setState(prev => ({
          ...prev,
          cart: response.cart,
          warnings: response.warnings || [],
          lastActionAt: new Date().toISOString(),
          lastShippingCalculation: new Date().toISOString(),
        }));

        // Refresh summary
        const summary = await cartService.getCartSummary(locale);
        setState(prev => ({ ...prev, summary }));

        // Retourner le nouveau quote pour usage immédiat
        const vendor = response.cart.itemsByVendor.find(
          v => v.vendorId === vendorId
        );
        return vendor?.shippingQuote || null;
      } catch (error: any) {
        console.error('Refresh shipping quote error:', error);
        return null;
      } finally {
        setState(prev => ({ ...prev, isRefreshingQuote: false }));
      }
    },
    [locale, executeWithRetry]
  );

  /**
   * 📍 Charger les points de service pour un vendeur
   */
  const loadServicePoints = useCallback(
    async (
      vendorId: string,
      useGeolocation: boolean = geolocationForServicePoints
    ): Promise<ServicePoint[]> => {
      if (!locale) return [];

      // Vérifier le cache
      const cached = state.servicePointsCache[vendorId];
      if (cached && enableShippingCache) {
        return cached;
      }

      setState(prev => ({
        ...prev,
        isCalculatingShipping: true,
        servicePointError: null,
      }));

      try {
        let userLocation: { lat: number; lng: number } | undefined;

        // Géolocalisation optionnelle
        if (
          useGeolocation &&
          typeof navigator !== 'undefined' &&
          navigator.geolocation
        ) {
          try {
            const position = await new Promise<GeolocationPosition>(
              (resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  timeout: 5000,
                  enableHighAccuracy: false,
                });
              }
            );
            userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
          } catch (geoError) {
            console.warn('Geolocation failed:', geoError);
          }
        }

        const servicePoints = await cartService.getServicePoints({
          vendorId,
          locale,
          userLocation,
        });

        // Mettre en cache
        setState(prev => ({
          ...prev,
          servicePointsCache: {
            ...prev.servicePointsCache,
            [vendorId]: servicePoints,
          },
        }));

        return servicePoints;
      } catch (error: any) {
        console.error('Load service points error:', error);
        setState(prev => ({
          ...prev,
          servicePointError:
            error.message || 'Erreur lors du chargement des points relais',
        }));
        return [];
      } finally {
        setState(prev => ({ ...prev, isCalculatingShipping: false }));
      }
    },
    [
      locale,
      state.servicePointsCache,
      enableShippingCache,
      geolocationForServicePoints,
    ]
  );

  // ============================================================================
  // 🔧 HELPERS & COMPUTED VALUES ÉTENDUS
  // ============================================================================

  const helpers = useMemo(
    () => ({
      // 💰 Formatage prix
      formatPrice: (amount: number, currency: string = 'EUR') =>
        new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(amount),

      // 📊 Calculs
      getTotalItems: () => state.cart?.itemsCount || 0,
      getTotalPrice: () => {
        if (state.cart?.estimatedTotalCents)
          return state.cart.estimatedTotalCents / 100;
        // Fallback: sum vendor totalCents
        if (state.cart?.itemsByVendor) {
          return (
            state.cart.itemsByVendor.reduce(
              (sum, v) => sum + (v.totalCents || 0),
              0
            ) / 100
          );
        }
        return 0;
      },
      getVendorCount: () => state.cart?.uniqueVendorsCount || 0,

      // ✅ Validations ÉTENDUES VINTED-LIKE
      canCheckout: () => {
        if (!state.cart || state.cart.itemsCount === 0) return false;
        if (state.summary?.hasUnavailableItems) return false;

        // Nouvelle validation Vinted : chaque vendeur doit être "ready"
        return state.cart.itemsByVendor.every(vendor => {
          if (!vendor.deliveryMethod) return false;

          if (
            (vendor.deliveryMethod?.type === 'relay' ||
              vendor.deliveryMethod?.type === 'locker') &&
            !vendor.selectedServicePoint
          ) {
            return false;
          }

          if (vendor.deliveryMethod?.type === 'home' && !vendor.homeAddress) {
            return false;
          }

          if (!vendor.shippingQuote) return false;

          return true;
        });
      },

      hasUnavailableItems: () => state.summary?.hasUnavailableItems || false,
      hasPriceChanges: () => state.summary?.hasPriceChanges || false,

      // 🆕 NOUVELLES VALIDATIONS VINTED
      hasUnreadyVendors: () => state.summary?.hasUnreadyVendors || false,
      getReadyVendorsCount: () => state.summary?.readyVendorsCount || 0,

      // Validation par vendeur
      isVendorReady: (vendorId: string): boolean => {
        const vendor = state.cart?.itemsByVendor.find(
          v => v.vendorId === vendorId
        );
        if (!vendor) return false;

        if (!vendor.deliveryMethod) return false;

        if (
          (vendor.deliveryMethod.type === 'relay' ||
            vendor.deliveryMethod.type === 'locker') &&
          !vendor.selectedServicePoint
        ) {
          return false;
        }

        if (vendor.deliveryMethod.type === 'home' && !vendor.homeAddress) {
          return false;
        }

        if (!vendor.shippingQuote) return false;

        const hasUnavailableItems = vendor.items.some(
          item => !item.productSnapshot.isStillAvailable
        );
        if (hasUnavailableItems) return false;

        return true;
      },

      // Vérification expiration quote
      isShippingQuoteExpired: (vendorId: string): boolean => {
        const vendor = state.cart?.itemsByVendor.find(
          v => v.vendorId === vendorId
        );
        return vendor?.lastQuoteAt
          ? isShippingCacheExpired(vendor.lastQuoteAt)
          : true;
      },

      // 🔍 Recherche
      findItemByProductId: (productId: string): CartItem | null => {
        if (
          !state.cart ||
          !state.cart.itemsByVendor ||
          !Array.isArray(state.cart.itemsByVendor)
        )
          return null;

        for (const vendor of state.cart.itemsByVendor) {
          const item = vendor.items.find(item => item.productId === productId);
          if (item) return item;
        }
        return null;
      },

      isProductInCart: (productId: string): boolean => {
        return helpers.findItemByProductId(productId) !== null;
      },

      getProductQuantity: (productId: string): number => {
        const item = helpers.findItemByProductId(productId);
        return item?.quantity || 0;
      },

      // 🆕 HELPERS VENDEURS
      findVendorById: (vendorId: string): CartVendorGroup | null => {
        return (
          state.cart?.itemsByVendor?.find(v => v.vendorId === vendorId) || null
        );
      },

      getTotalItemsByVendor: (vendorId: string): number => {
        const vendor = helpers.findVendorById(vendorId);
        return (
          vendor?.items?.reduce((total, item) => total + item.quantity, 0) || 0
        );
      },

      getVendorTotal: (vendorId: string): number => {
        const vendor = helpers.findVendorById(vendorId);
        return vendor?.totalCents ? vendor.totalCents / 100 : 0;
      },

      getVendorShipping: (vendorId: string): number => {
        const vendor = helpers.findVendorById(vendorId);
        return vendor?.shippingCostCents ? vendor.shippingCostCents / 100 : 0;
      },

      // Service points en cache
      getCachedServicePoints: (vendorId: string): ServicePoint[] => {
        return state.servicePointsCache[vendorId] || [];
      },
    }),
    [
      state.cart,
      state.summary,
      state.servicePointsCache,
      locale,
      isShippingCacheExpired,
    ]
  );

  // ============================================================================
  // 🔄 EFFECTS ÉTENDUS
  // ============================================================================

  // Chargement initial
  useEffect(() => {
    if (locale && isAuthenticated && user) {
      loadCart();
    }
  }, [locale, isAuthenticated, user]);

  // Auto-sync périodique
  useEffect(() => {
    if (!autoSync || !locale) return;

    syncIntervalRef.current = setInterval(() => {
      if (!state.isLoading && !state.isAddingItem && !state.isUpdatingItem) {
        loadCart();
      }
    }, 10000); // 10 secondes

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [
    autoSync,
    locale,
    loadCart,
    state.isLoading,
    state.isAddingItem,
    state.isUpdatingItem,
  ]);
  // 🔄 POLLING AUTOMATIQUE TOUTES LES 30 SECONDES
  useEffect(() => {
    if (!isAuthenticated || !locale) return;

    pollingIntervalRef.current = setInterval(() => {
      loadCart();
    }, 120_000); // 2 minutes (was 30s — caused rate limiting in prod)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isAuthenticated, locale, loadCart]);

  // Sync entre instances de useCart : quand une instance fait une mutation
  // (addToCart, updateQuantity, removeItem), elle dispatch un event
  // 'glowp:cart-changed' que toutes les autres instances ecoutent pour
  // re-fetch. Sans ca, le CartIndicator du Layout ne se rafraichit pas
  // quand ProductDetail ajoute au panier (chaque useCart a son state local).
  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = () => {
      loadCart();
    };
    window.addEventListener('glowp:cart-changed', handler);
    return () => window.removeEventListener('glowp:cart-changed', handler);
  }, [isAuthenticated, loadCart]);

  // 🆕 Auto-refresh quotes périodique
  useEffect(() => {
    if (!autoRefreshQuotes || !locale || !state.cart) return;

    quoteRefreshIntervalRef.current = setInterval(
      async () => {
        if (state.isRefreshingQuote || state.isLoading) return;

        // Refresh quotes expirés seulement
        const vendorsWithExpiredQuotes = state.cart!.itemsByVendor.filter(
          vendor =>
            vendor.shippingQuote &&
            helpers.isShippingQuoteExpired(vendor.vendorId)
        );

        for (const vendor of vendorsWithExpiredQuotes) {
          try {
            await refreshShippingQuote(vendor.vendorId);
          } catch (error) {
            console.warn(
              `Failed to refresh quote for vendor ${vendor.vendorId}:`,
              error
            );
          }
        }
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => {
      if (quoteRefreshIntervalRef.current) {
        clearInterval(quoteRefreshIntervalRef.current);
      }
    };
  }, [
    autoRefreshQuotes,
    locale,
    state.cart,
    state.isRefreshingQuote,
    state.isLoading,
    helpers.isShippingQuoteExpired,
    refreshShippingQuote,
  ]);

  // Nettoyage
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      if (quoteRefreshIntervalRef.current) {
        clearInterval(quoteRefreshIntervalRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // ============================================================================
  // 📊 COMPUTED STATES ÉTENDUS
  // ============================================================================

  const computed = useMemo(
    () => ({
      // États généraux
      isEmpty: !state.cart || state.cart.itemsCount === 0,
      isLoading: state.isLoading || state.isSyncing,
      hasErrors: !!(
        state.loadError ||
        state.addError ||
        state.updateError ||
        state.removeError ||
        state.generalError ||
        state.deliveryMethodError ||
        state.servicePointError ||
        state.shippingQuoteError
      ),
      hasWarnings: state.warnings.length > 0,

      // États d'action
      isUpdating:
        state.isAddingItem ||
        state.isUpdatingItem ||
        state.isRemovingItem ||
        state.isSelectingShipping ||
        state.isClearing ||
        state.isCalculatingShipping ||
        state.isSelectingServicePoint ||
        state.isSettingDeliveryMethod ||
        state.isRefreshingQuote,

      // 🆕 NOUVEAUX ÉTATS SPÉCIFIQUES VINTED
      isShippingOperation:
        state.isCalculatingShipping ||
        state.isSelectingServicePoint ||
        state.isSettingDeliveryMethod ||
        state.isRefreshingQuote,

      // Données dérivées
      cart: state.cart,
      summary: state.summary,
      warnings: state.warnings,
      errors: {
        load: state.loadError,
        add: state.addError,
        update: state.updateError,
        remove: state.removeError,
        shipping: state.shippingError,
        general: state.generalError,
        // 🆕 NOUVELLES ERREURS
        deliveryMethod: state.deliveryMethodError,
        servicePoint: state.servicePointError,
        shippingQuote: state.shippingQuoteError,
      },

      // 🆕 CACHE ET MÉTRIQUES SHIPPING
      servicePointsCache: state.servicePointsCache,
      lastShippingCalculation: state.lastShippingCalculation,
    }),
    [state]
  );

  // ============================================================================
  // 🎯 RETURN HOOK API ÉTENDUE
  // ============================================================================

  return {
    // 📊 État principal
    ...computed,

    // 🛒 Actions principales existantes
    addToCart,
    updateCartItem,
    removeFromCart,
    selectShippingMethod,
    clearCart,
    refreshCart,
    loadCart,

    // 🆕 NOUVELLES ACTIONS VINTED-LIKE
    setDeliveryMethod,
    setServicePoint,
    setHomeAddress,
    refreshShippingQuote,
    loadServicePoints,

    // 🔧 Helpers étendus
    helpers,

    // 📈 Métriques pour debugging étendues
    metrics: {
      retryCount: state.retryCount,
      lastActionAt: state.lastActionAt,
      lastShippingCalculation: state.lastShippingCalculation,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      cacheSize: Object.keys(state.servicePointsCache).length,
      shippingCacheEnabled: enableShippingCache,
      autoRefreshEnabled: autoRefreshQuotes,
    },
  };
};

export type UseCartReturn = ReturnType<typeof useCart>;
